/**
 * 上下行流量计量（按日聚合，落 `traffic` store）—— ARCH §3.6。
 *
 * 计量口径：每条经中继 / 直连收发的数据按 **帧字节数** 计入当天 `upBytes` / `downBytes`。
 * 服务于设置页「今日流量」与中继计费展示（计费常量见 `@shared/chain`）。
 */

import { dayKey } from '@/lib/format';
import { createLogger } from '@/lib/logger';
import type { DayTraffic } from '@shared/types';

const logger = createLogger('net:traffic');

/** 落库节流：累计变化超过该阈值或超过该时长才写一次 IndexedDB。 */
const FLUSH_THRESHOLD_BYTES = 8 * 1024;
const FLUSH_INTERVAL_MS = 5_000;

export interface TrafficSink {
  get(key: string): Promise<DayTraffic | undefined>;
  put(value: DayTraffic): Promise<void>;
}

/**
 * 流量计量器。
 * 纯计数 + 节流落库；不依赖 libp2p，便于单测。
 */
export class TrafficMeter {
  private up = 0;
  private down = 0;
  private dirty = 0;
  private lastFlushAt = 0;
  private readonly key = dayKey();

  constructor(private readonly sink?: TrafficSink) {}

  /** 记录上行字节。 */
  recordUp(bytes: number): void {
    this.up += Math.max(0, Math.floor(bytes));
    this.dirty += bytes;
    void this.maybeFlush();
  }

  /** 记录下行字节。 */
  recordDown(bytes: number): void {
    this.down += Math.max(0, Math.floor(bytes));
    this.dirty += bytes;
    void this.maybeFlush();
  }

  /** 当天快照（含未落库的增量）。 */
  snapshot(): DayTraffic {
    return { dayKey: this.key, upBytes: this.up, downBytes: this.down };
  }

  /** 强制落库（页面隐藏 / 关闭前调用）。 */
  async flush(force = false): Promise<void> {
    if (!this.sink) return;
    if (!force && this.dirty === 0) return;
    try {
      const existing = await this.sink.get(this.key);
      await this.sink.put({
        dayKey: this.key,
        upBytes: Math.max(this.up, existing?.upBytes ?? 0),
        downBytes: Math.max(this.down, existing?.downBytes ?? 0),
      });
      this.dirty = 0;
      this.lastFlushAt = Date.now();
    } catch (err) {
      logger.warn('流量落库失败', err instanceof Error ? err.message : err);
    }
  }

  /** 读取当天累计（含历史落库值，取较大者，防多标签页互相覆盖）。 */
  async today(): Promise<DayTraffic> {
    if (!this.sink) return this.snapshot();
    try {
      const stored = await this.sink.get(this.key);
      return {
        dayKey: this.key,
        upBytes: Math.max(this.up, stored?.upBytes ?? 0),
        downBytes: Math.max(this.down, stored?.downBytes ?? 0),
      };
    } catch {
      return this.snapshot();
    }
  }

  private async maybeFlush(): Promise<void> {
    const now = Date.now();
    if (this.dirty >= FLUSH_THRESHOLD_BYTES || now - this.lastFlushAt >= FLUSH_INTERVAL_MS) {
      await this.flush();
    }
  }
}

export default TrafficMeter;
